import React from 'react';
import { AlertCircle, CheckCircle } from 'lucide-react';

const FormFieldsViewer = ({
    content,
    responses = {},
    onResponsesChange,
    readOnly = false,
    showValidation = false,
    validationErrors = {}
}) => {
    const fields = content?.fields || [];
    const instructions = content?.instructions || '';

    const handleFieldChange = (fieldId, value) => {
        if (readOnly) return;
        onResponsesChange({ ...responses, [fieldId]: value });
    };

    const handleCheckboxChange = (fieldId, option, checked) => {
        if (readOnly) return;
        const current = responses[fieldId] || [];
        const arr = Array.isArray(current) ? current : [];
        const updated = checked ? [...arr, option] : arr.filter(v => v !== option);
        onResponsesChange({ ...responses, [fieldId]: updated });
    };

    const hasError = (fieldId) => showValidation && validationErrors[fieldId];
    const getFieldValue = (fieldId) => responses[fieldId] || '';

    if (fields.length === 0) {
        return (
            <div className="bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 rounded-lg p-6 text-center">
                <p className="text-gray-500 dark:text-slate-400">No form fields configured for this section.</p>
            </div>
        );
    }

    const renderField = (field, index) => {
        const fieldValue = getFieldValue(field.id);
        const fieldHasError = hasError(field.id);
        const isComplete = field.type === 'checkbox'
            ? Array.isArray(fieldValue) && fieldValue.length > 0
            : fieldValue && String(fieldValue).trim() !== '';

        const baseInputClass = `w-full px-4 py-2.5 border rounded-lg text-sm transition-all ${
            readOnly
                ? 'bg-gray-50 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border-gray-200 dark:border-slate-700 cursor-not-allowed'
                : 'bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent'
        } ${fieldHasError ? 'border-red-400 bg-red-50 dark:bg-red-500/5' : ''}`;

        return (
            <div key={field.id} className="relative">
                {/* Label */}
                <label htmlFor={field.id} className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-slate-100 mb-2">
                    <span>{field.label || `Field ${index + 1}`}</span>
                    {field.required && <span className="text-red-500 text-xs">*</span>}
                    {isComplete && !readOnly && <CheckCircle className="w-4 h-4 text-green-500" />}
                </label>

                {/* Field Renderer */}
                {(() => {
                    switch (field.type) {
                        case 'text':
                        case 'text_input': // backwards compat
                            return (
                                <input id={field.id} type="text" value={fieldValue} onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                    placeholder={field.placeholder || 'Enter text...'} disabled={readOnly} className={baseInputClass} />
                            );
                        case 'long_text':
                        case 'text_area': // backwards compat
                            return (
                                <textarea id={field.id} value={fieldValue} onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                    placeholder={field.placeholder || 'Enter your response...'} disabled={readOnly} rows="4" className={`${baseInputClass} resize-none`} />
                            );
                        case 'email':
                            return (
                                <input id={field.id} type="email" value={fieldValue} onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                    placeholder={field.placeholder || 'name@example.com'} disabled={readOnly} className={baseInputClass} />
                            );
                        case 'phone':
                            return (
                                <input id={field.id} type="tel" value={fieldValue} onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                    placeholder={field.placeholder || '+1 (555) 000-0000'} disabled={readOnly} className={baseInputClass} />
                            );
                        case 'number':
                            return (
                                <input id={field.id} type="number" value={fieldValue} onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                    placeholder={field.placeholder || '0'} disabled={readOnly} className={baseInputClass} />
                            );
                        case 'date':
                            return (
                                <input id={field.id} type="date" value={fieldValue} onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                    disabled={readOnly} className={baseInputClass} />
                            );
                        case 'dropdown':
                            return (
                                <select id={field.id} value={fieldValue} onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                    disabled={readOnly} className={baseInputClass}>
                                    <option value="">{field.placeholder || 'Select an option...'}</option>
                                    {(field.options || []).filter(o => o).map((option, i) => (
                                        <option key={i} value={option}>{option}</option>
                                    ))}
                                </select>
                            );
                        case 'checkbox': {
                            const checked = Array.isArray(fieldValue) ? fieldValue : [];
                            return (
                                <div className="space-y-2 mt-1">
                                    {(field.options || []).filter(o => o).map((option, i) => (
                                        <label key={i} className="flex items-center gap-3 cursor-pointer group">
                                            <input
                                                type="checkbox"
                                                checked={checked.includes(option)}
                                                onChange={(e) => handleCheckboxChange(field.id, option, e.target.checked)}
                                                disabled={readOnly}
                                                className="w-4 h-4 text-primary rounded border-gray-300 dark:border-slate-600 focus:ring-primary"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-slate-300 group-hover:text-gray-900 dark:group-hover:text-white">{option}</span>
                                        </label>
                                    ))}
                                    {(!field.options || field.options.filter(o => o).length === 0) && (
                                        <p className="text-sm text-gray-400 dark:text-slate-500 italic">No options configured</p>
                                    )}
                                </div>
                            );
                        }
                        case 'radio':
                            return (
                                <div className="space-y-2 mt-1">
                                    {(field.options || []).filter(o => o).map((option, i) => (
                                        <label key={i} className="flex items-center gap-3 cursor-pointer group">
                                            <input
                                                type="radio"
                                                name={field.id}
                                                value={option}
                                                checked={fieldValue === option}
                                                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                                disabled={readOnly}
                                                className="w-4 h-4 text-primary border-gray-300 dark:border-slate-600 focus:ring-primary"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-slate-300 group-hover:text-gray-900 dark:group-hover:text-white">{option}</span>
                                        </label>
                                    ))}
                                    {(!field.options || field.options.filter(o => o).length === 0) && (
                                        <p className="text-sm text-gray-400 dark:text-slate-500 italic">No options configured</p>
                                    )}
                                </div>
                            );
                        default:
                            return (
                                <input id={field.id} type="text" value={fieldValue} onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                    placeholder={field.placeholder || 'Enter text...'} disabled={readOnly} className={baseInputClass} />
                            );
                    }
                })()}

                {/* Error Message */}
                {fieldHasError && (
                    <div className="flex items-center gap-1.5 mt-2 text-red-600 dark:text-red-400">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm">{validationErrors[field.id]}</span>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {instructions && (
                <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/30 rounded-lg p-4">
                    <p className="text-sm text-blue-800 dark:text-blue-300">{instructions}</p>
                </div>
            )}

            <div className="space-y-5">
                {fields.map((field, index) => renderField(field, index))}
            </div>

            {readOnly && Object.keys(responses).length > 0 && (
                <div className="bg-green-50 dark:bg-green-500/10 border border-green-100 dark:border-green-500/30 rounded-lg p-4 mt-4">
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Form completed</span>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Validate form fields - returns object with field IDs as keys and error messages as values
 */
export const validateFormFields = (fields, responses) => {
    const errors = {};
    if (!fields || fields.length === 0) return errors;

    fields.forEach(field => {
        if (field.required) {
            const value = responses[field.id];
            if (field.type === 'checkbox') {
                if (!value || !Array.isArray(value) || value.length === 0) {
                    errors[field.id] = `${field.label || 'This field'} is required`;
                }
            } else {
                if (!value || String(value).trim() === '') {
                    errors[field.id] = `${field.label || 'This field'} is required`;
                }
            }
        }
    });

    return errors;
};

/**
 * Package form responses for submission
 */
export const packageFormResponses = (fields, responses) => {
    if (!fields || fields.length === 0) return [];

    return fields.map(field => ({
        field_id: field.id,
        label: field.label,
        type: field.type,
        value: responses[field.id] || (field.type === 'checkbox' ? [] : ''),
        required: field.required
    }));
};

export default FormFieldsViewer;
